# Route: /materials/definitions/new
# Create a new material definition — dedicated full-width create page
# (lot-receive layout idiom). Replaces the former def-list create modal.
# Audience: MES Engineers (md+), blocked on sm

page-def-create:
  type: Page
  archetype: create
  meta:
    title: $t.def_create.meta_title
    auth: required
  data:
    classes:
      model: ItemClass[]
      refresh: on_mount
    # F75 #139 (AC-P2) — controlled UoM vocabulary for the base-unit select.
    uom:
      model: UnitOfMeasure[]
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - def-create-sm-notice
    - def-create-content

# ── sm viewport notice ───────────────────────────────────────────────────────

def-create-sm-notice:
  type: SmNotice
  content: $t.common.sm_notice_def

# ── Main content (hidden on sm) ──────────────────────────────────────────────

def-create-content:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    responsive:
      sm:
        display: none
  children:
    - def-create-header
    - def-create-form

# ── Header ───────────────────────────────────────────────────────────────────

def-create-header:
  type: PageHeader
  content: $t.def_create.title
  back_route: /materials/definitions

# ── Create form ──────────────────────────────────────────────────────────────
# Full-width Form (no max_width cap — one form-width rule everywhere).
# Input meta applies touch-safe min_height, a focus outline, required indicator +
# validation timing; authors do not spell these out per field. The focus ring on a
# form control comes from InputCssGenerator.cs:96-99 (`input:focus, textarea:focus,
# select:focus`), which keys on :focus rather than :focus-visible, so it also shows
# on mouse click. The :host(:focus-visible) rule FocusCssGenerator writes does NOT
# render — see design-system/accessibility.md § Focus visible ring.

def-create-form:
  type: Form
  model: ItemDefinition
  mode: create
  a11y:
    label: $t.def_create.form_label
  action:
    type: create
    endpoint: /api/items/definitions
    on_success: navigate(/materials/definitions)
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - def-create-field-name
    - def-create-field-class
    - def-create-field-tracking
    - def-create-field-unit
    - def-create-field-shelf-life
    - def-create-field-make-buy
    - def-create-field-description
    - def-create-form-actions

def-create-field-name:
  type: Input
  input_type: text
  bind:
    value: name
  label: $t.def_list.col_name
  required: true
  a11y:
    label: $t.def_list.col_name

def-create-field-class:
  type: Input
  input_type: select
  bind:
    value: itemClassId
    options: classes
  label: $t.def_list.col_class
  required: true
  a11y:
    label: $t.def_list.col_class

def-create-field-tracking:
  type: Input
  input_type: select
  bind:
    value: trackingMode
  label: $t.def_list.col_tracking
  required: true
  a11y:
    label: $t.def_list.col_tracking

# F75 #139 (AC-P2) — base UoM is a controlled-vocabulary select (no free text),
# bound to GET /api/items/units-of-measure. Option value = unit code (id).
def-create-field-unit:
  type: Input
  input_type: select
  bind:
    value: baseUnitOfMeasure
    options: uom
  label: $t.def_list.col_unit
  required: true
  a11y:
    label: $t.def_list.col_unit

def-create-field-shelf-life:
  type: Input
  input_type: number
  bind:
    value: shelfLifeDays
  label: $t.def_list.col_shelf_life
  a11y:
    label: $t.def_list.col_shelf_life

def-create-field-make-buy:
  type: Input
  input_type: select
  bind:
    value: makeOrBuy
  label: $t.def_list.col_make_buy
  required: true
  a11y:
    label: $t.def_list.col_make_buy

def-create-field-description:
  type: Input
  input_type: textarea
  bind:
    value: description
  label: $t.def_create.field_description
  a11y:
    label: $t.def_create.field_description

# FormActions meta: Cancel (outlined secondary) then Create (filled primary)
# in one right-aligned row; sticky-bottom on sm/md, inline on lg+.

def-create-form-actions:
  type: FormActions
  submit_label: $t.def_list.btn_create
  cancel_route: /materials/definitions
